import { parseJsonOrNull } from "replo-utils/lib/json";
import { z } from "zod";

const SEED_STORAGE_KEY = "replo_marketing_seed";

// Long enough to survive a slow signup, short enough that an old seed never fires.
const SEED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// storedAt is required, so entries written before the TTL shipped read as expired.
const marketingSeedSchema = z
  .object({
    prompt: z.string(),
    jti: z.string().nullable(),
    hasImage: z.boolean(),
    storedAt: z.number(),
  })
  .refine(({ storedAt }) => Date.now() - storedAt < SEED_MAX_AGE_MS);

type MarketingSeedData = z.infer<typeof marketingSeedSchema>;

export function storeMarketingSeed(data: Omit<MarketingSeedData, "storedAt">) {
  localStorage.setItem(
    SEED_STORAGE_KEY,
    JSON.stringify({ ...data, storedAt: Date.now() }),
  );
}

/** Returns the seed worth acting on, purging it when stale or unreadable. */
export function readFreshMarketingSeedAndPurgeStale(): MarketingSeedData | null {
  const stored = localStorage.getItem(SEED_STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  const seed = marketingSeedSchema.safeParse(parseJsonOrNull(stored));
  if (!seed.success) {
    clearMarketingSeed();
    return null;
  }

  return seed.data;
}

export function clearMarketingSeed() {
  localStorage.removeItem(SEED_STORAGE_KEY);
}
